// ============================================================
// Feedback Types
// ============================================================

export enum FeedbackHistoryType {
  RATING = "rating",
  FEEDBACK = "feedback",
  CONTACT = "contact",
}

export enum ContactType {
  HOW_TO = "how_to",
  BUG = "bug",
  REQUEST = "request",
  OTHER = "other",
}

export interface FeedbackHistoryItem {
  id: string;
  type: FeedbackHistoryType;
  created_at: string;
  rating?: number;
  complaint_type?: string;
  detail?: string;
  contact_type?: ContactType;
  reply?: {
    title: string;
    content: string;
    published_at: string;
  } | null;
}

// ============================================================
// User Activity Types
// ============================================================

export const UserActivityType = {
  COURSE_LEVEL_CLEAR: "course_level_clear",
  PROJECT_CREATED: "project_created",
  PROJECT_PUBLISHED: "project_published",
  PROJECT_FAVORITED: "project_favorited",
  PROJECT_FAVORITED_RECEIVED: "project_favorited_received",
  LOGIN: "login",
  ICON_CHANGED: "icon_changed",
  AI_CHAT_SESSION_STARTED: "ai_chat_session_started",
  INQUIRY_SENT: "inquiry_sent",
  APP_REVIEWED: "app_reviewed",
  FEEDBACK_SENT: "feedback_sent",
  COIN_EARNED: "coin_earned",
  COIN_SPENT: "coin_spent",
  LOGIN_STREAK_MILESTONE: "login_streak_milestone",
} as const;

export type UserActivityTypeValue = (typeof UserActivityType)[keyof typeof UserActivityType];

export interface CourseClearActivityData {
  course_id: number;
  course_type: string;
  stage_id: number;
  level_id: number;
  is_first_clear: boolean;
  used_tips: boolean;
}

export interface ProjectActivityData {
  project_id: string;
  project_name: string;
  project_type?: string;
  author_uid?: string;
  favorited_by_uid?: string;
}

export interface LoginActivityData {
  login_env: string;
  login_count: number;
}

export interface IconChangedActivityData {
  old_icon: string | null;
  new_icon: string;
}

export interface CoinActivityData {
  amount: number;
  source: string;
  new_balance: number;
}

export interface LoginStreakMilestoneActivityData {
  milestone_days: number;
  bonus_coins: number;
  current_streak: number;
}

export type ActivityData =
  | CourseClearActivityData
  | ProjectActivityData
  | LoginActivityData
  | IconChangedActivityData
  | CoinActivityData
  | LoginStreakMilestoneActivityData
  | Record<string, unknown>;

export interface UserActivityLog {
  id: number;
  uid: string;
  activity_type: UserActivityTypeValue;
  activity_data: ActivityData | null;
  activity_at: string;
  created_at: string;
}

export interface GetActivitiesResponse {
  activities: UserActivityLog[];
  total: number;
  has_more: boolean;
}

// ============================================================
// AI Chat Types
// ============================================================

export interface AIChatSession {
  id: string;
  title: string;
  message_count: number;
  last_message: {
    content: string;
    role: "user" | "assistant";
    created_at: string;
  } | null;
  created_at: string;
}

export interface GetAIChatSessionsResponse {
  sessions: AIChatSession[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AIChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  image_urls: string[] | null;
  created_at: string;
}

export interface GetAIChatMessagesResponse {
  session: {
    id: string;
    child_uid: string;
    title: string;
    created_at: string;
  };
  messages: AIChatMessage[];
  has_more: boolean;
}

// ============================================================
// Course types (スタブ)
// ============================================================

export enum Course {
  BASIC = "basic",
  ADVANCED = "advanced",
  ALGORITHM = "algorithm",
  PROGRAM = "program",
}

export type CourseStep = {
  id: number;
  name: string;
  description: string;
};

export type CourseStage = {
  id: number;
  name: string;
  steps: CourseStep[];
};

export type CourseData = {
  id: string;
  name: string;
  stages: CourseStage[];
};

// Stub utils for courses feature (not used in parents)
export const getCourseStepName = (
  _course: string,
  _stageId: number,
  _stepId: number
) => {
  return "";
};

export const getCourseStageName = (_course: string, _stageId: number) => {
  return "";
};

export const string2Course = (courseString: string): Course => {
  return courseString as Course;
};

// ============================================================
// Creator types (moved from features/creator)
// ============================================================

export enum ProjectType {
  BASIC = "basic",
  MAP = "map",
  TEMPLATE = "template",
}

export enum StockTabIndex {
  STAMP = 0,
  EFFECT = 1,
  MUSIC = 2,
  BACKGROUND = 3,
  ALBUM = 4,
  DRAWING = 5,
  AI = 6,
}

export type ProjectEntity = {
  project_id: string;
  inquiry_id: string;
  type: ProjectType;
  tags: string;
  category: string;
  author_uid: string;
  author_name: string;
  author_icon: string;
  copied_project_id: string;
  original_project_id: string;
  original_project_created_at: string;
  original_author_uid: string;
  original_author_name: string;
  original_author_icon: string;
  name: string;
  version: number;
  description: string;
  capture?: string;
  cover?: string;
  blockly_capture: string;
  reaction: any;
  published: boolean;
  copied: number;
  favorited: number;
  viewed: number;
  enable_copied: boolean;
  is_favorited: boolean;
  created_time: string;
  updated_time: string;
  deleted_at?: string;
};

// ============================================================
// Generation types (moved from features/generation)
// ============================================================

export enum GenerationStatus {
  IDLE = "idle",
  GENERATING = "generating",
  COMPLETE = "complete",
  ERROR = "error",
}

export type GenerationResult = {
  url: string;
  status: GenerationStatus;
};

export type AIGenerationState = {
  status: GenerationStatus;
  progress: number;
  partialImage: string | null;
  results: string[];
  error: string | null;
};
